import {
  IProps,
  KeyPairs,
} from '~editor/ActionEditor/ActionsComponents/KeybindActionComponent'
import React, { useEffect, useMemo, useState } from 'react'
import { AnyActionType, KeybindAction } from '~core/forge.type'
import { Box } from '~core/components'
import { Select } from '~editor/components/Select'
import { useDispatch } from 'react-redux'
import { useKeyboardLayoutMap } from '~core/hooks/useKeyboardLayoutMap'
import { MEDIA_KEYS, META_KEYS, SPECIAL_KEYS } from '~core/utils/keyboard'

export const DropdownKeybindComponent = ({
  action,
  actions,
  activeActionGesture,
}: IProps) => {
  const dispatch = useDispatch()
  const [first, setFirst] = useState<KeyPairs | null>()
  const [second, setSecond] = useState<KeyPairs | null>()
  const [third, setThird] = useState<KeyPairs | null>()
  const keyboardLayoutMap = useKeyboardLayoutMap()

  const keys: KeyPairs[] = useMemo(() => {
    if (!keyboardLayoutMap) {
      return []
    }

    let keyboardKeys: KeyPairs[] = Array.from(keyboardLayoutMap.entries()).map(
      ([code, key]) => ({
        code,
        key: key.match(/^[A-Za-z]$/) ? key.toUpperCase() : key,
      }),
    )

    return keyboardKeys.sort((a, b) => {
      const keyA = a.key
      const keyB = b.key

      if (keyA.match(/^[A-Za-z]$/) && keyB.match(/^[A-Za-z]$/)) {
        return keyA.localeCompare(keyB) // Sort alphabetically
      }
      if (keyA.match(/^\d$/) && keyB.match(/^\d$/)) {
        return keyA.localeCompare(keyB) // Sort numerically
      }
      if (keyA.match(/^[A-Za-z]$/)) {
        return -1 // Letters come first
      }
      if (keyB.match(/^[A-Za-z]$/)) {
        return 1 // Letters come first
      }
      if (keyA.match(/^\d$/)) {
        return -1 // Numbers come before symbols
      }
      if (keyB.match(/^\d$/)) {
        return 1 // Numbers come before symbols
      }
      return keyA.localeCompare(keyB) // Sort symbols
    })
  }, [keyboardLayoutMap])

  const showSecond = useMemo(() => {
    return !!META_KEYS.find((media_key) => media_key.key == first?.key)
  }, [first])

  const showThird = useMemo(() => {
    return !!META_KEYS.find((media_key) => media_key.key == second?.key)
  }, [second])

  const firstValue = useMemo(() => {
    if (!first) return ''
    return { label: first?.key, value: first?.code }
  }, [first])

  const secondValue = useMemo(() => {
    if (!second) return ''
    return { label: second?.key, value: second?.code }
  }, [second])

  const thirdValue = useMemo(() => {
    if (!third) return ''
    return { label: third?.key, value: third?.code }
  }, [third])

  useEffect(() => {
    if (action.keys.length == 0) {
      setFirst(null)
      setSecond(null)
      setThird(null)
      return
    }

    const [firstCode, secondCode, thirdCode] = action.keys

    const ALL_KEYS = Array().concat(META_KEYS, keys, SPECIAL_KEYS, MEDIA_KEYS)

    firstCode &&
      setFirst(
        ALL_KEYS.find((keyItem) => keyItem.code === firstCode) || {
          key: firstCode,
          code: firstCode,
        },
      )
    secondCode &&
      setSecond(
        ALL_KEYS.find((keyItem) => keyItem.code === secondCode) || {
          key: secondCode,
          code: secondCode,
        },
      )
    thirdCode &&
      setThird(
        ALL_KEYS.find((keyItem) => keyItem.code === thirdCode) || {
          key: thirdCode,
          code: thirdCode,
        },
      )
  }, [actions, action, keys])

  useEffect(() => {
    const keysSet = [first, second, third].filter(
      (item) => !!item,
    ) as KeyPairs[]

    const newKeys = keysSet.map(({ code }) => {
      return code
    })

    if (!newKeys.length) return

    const [firstCode, secondCode, thirdCode] = action.keys

    if (
      firstCode === first?.code &&
      secondCode === second?.code &&
      thirdCode === third?.code
    ) {
      return
    }

    dispatch.nodes.updateActions({
      actionGestureType: activeActionGesture,
      actions: actions.map((_a: AnyActionType) => {
        const _action = { ..._a } as KeybindAction

        if (action.id === _action.id) {
          _action.keys = Array.from(new Set(newKeys))
        }
        return _action
      }),
    })
  }, [first, second, third])

  return (
    <Box display="flex" alignItems="center">
      <Select
        minWidth="80px"
        height="32px"
        styles={{
          valueContainer: () => ({
            paddingRight: '6px',
          }),
        }}
        value={firstValue}
        onChange={(option) => {
          if (!option) return
          const { label, value }: { label: string; value: string } =
            option as any
          setFirst({ key: label, code: value })
          setSecond(null)
          setThird(null)
        }}
        options={Array()
          .concat(META_KEYS, keys, SPECIAL_KEYS, MEDIA_KEYS)
          .map((keyPair) => ({ label: keyPair.key, value: keyPair.code }))}
        isSearchable={true}
        isDisabled={false}
      />
      {showSecond && (
        <>
          <Box
            as="span"
            height="22px"
            margin="0 7px"
            lineHeight="1"
            fontSize="20px"
          >
            {'+'}
          </Box>
          <Select
            minWidth="80px"
            height="32px"
            styles={{
              valueContainer: () => ({
                paddingRight: '6px',
              }),
            }}
            value={secondValue}
            onChange={(option) => {
              if (!option) return
              const { label, value }: { label: string; value: string } =
                option as any
              setSecond({ key: label, code: value })
              setThird(null)
            }}
            options={Array()
              .concat(META_KEYS, keys, SPECIAL_KEYS)
              .filter(({ key }) => key !== first?.key)
              .map((keyPair) => ({ label: keyPair.key, value: keyPair.code }))}
            isSearchable={true}
            isDisabled={false}
          />
        </>
      )}
      {showThird && (
        <>
          <Box
            as="span"
            height="22px"
            margin="0 7px"
            lineHeight="1"
            fontSize="20px"
          >
            {'+'}
          </Box>
          <Select
            minWidth="80px"
            height="32px"
            styles={{
              valueContainer: () => ({
                paddingRight: '6px',
              }),
            }}
            value={thirdValue}
            onChange={(option) => {
              if (!option) return
              const { label, value }: { label: string; value: string } =
                option as any
              setThird({ key: label, code: value })
            }}
            options={Array()
              .concat(keys, SPECIAL_KEYS)
              .map((keyPair) => ({ label: keyPair.key, value: keyPair.code }))}
            isSearchable={true}
            isDisabled={false}
          />
        </>
      )}
    </Box>
  )
}
